import { useCallback, useEffect, useState } from 'react';
import { CareGrid } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { slicerParam, sortParam, type SlicerDef } from '@lib/grid';
import { fmtMemberId } from '@lib/member-id';
import { listReviewBatches, getReviewBatch } from '@lib/care-api';
import { NotByCampaignNote } from '@components/CampaignScopeNote';
import { ArchiveTabs } from '@components/ArchiveTabs';
import {
  ACTION_META,
  fmtDate,
  fmtMinutes,
  fmtTs,
  type ActionType,
  type ReviewBatchRow,
  type ReviewBatchSortKey,
  type ReviewBatchSummary,
} from '@lib/care-model';

/**
 * Archive (nav 07, William 2026-07-08) - the read-only history of every package sent to the Portal.
 * Each Final Review batch approval files a dated station-archive bundle (review-approve-batch.ts, the
 * Printing-Module pattern); this lists those bundles newest-first and expands one to its member
 * manifest. Mirrors how the Printing Module (Batches) and Lab Intake (Archive) surface their sent
 * work. Read-only, no actions; PHI-light (member display name + stripped id, never the full snapshot).
 */

const actionLabel = (t: string): string => ACTION_META[t as ActionType]?.label ?? t;

function ActionMix({ actions }: { actions: ReviewBatchSummary['actions'] }) {
  // An approved event with no business actions was handled and closed without one; say so
  // instead of a bare "-" (William feedback, carried from rev27).
  if (!actions.length) return <span style={{ color: 'var(--ink-3)' }}>Handled - no action</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {actions.map((a) => (
        <span
          key={a.type}
          title={actionLabel(a.type)}
          style={{
            display: 'inline-block',
            padding: '1px 8px',
            borderRadius: 999,
            border: '1px solid var(--rule)',
            background: 'var(--paper-2)',
            fontSize: '0.82em',
            whiteSpace: 'nowrap',
          }}
        >
          {a.count}&times; {actionLabel(a.type)}
        </span>
      ))}
    </span>
  );
}

/** The short reviewer display: "Faidi, William <email>" -> "Faidi, William". Falls back to the email. */
function shortName(who: string): string {
  const lt = who.indexOf('<');
  return lt > 0 ? who.slice(0, lt).trim() : who.trim();
}

/**
 * Column sorting (William 2026-08-03). The direction a column opens in: newest/biggest first for a
 * date or a number, A-Z for a name. Re-clicking the active column flips it.
 */
/** The keys the index route sorts by (row 356: up to three of them, Shift-click on a heading). */
const ARCHIVE_SORT_KEYS: readonly ReviewBatchSortKey[] = ['approved', 'bundle', 'members', 'dispositions', 'operators', 'reviewers', 'tat'];

/**
 * ROW 356: the Archive's slicers, answered by the SERVER over the whole archive (the screen holds a
 * page of 60): what was done, who handled it, who reviewed it, and whether the manifest was read.
 */
const ARCHIVE_SLICERS: SlicerDef<ReviewBatchSummary>[] = [
  { key: 'dispositions', label: 'Dispositions', value: () => null, labelFor: actionLabel, blankLabel: 'Handled - no action' },
  { key: 'operators', label: 'Handled by', value: () => null, labelFor: shortName, blankLabel: 'Nobody named' },
  { key: 'reviewers', label: 'Reviewed by', value: () => null, labelFor: shortName, blankLabel: 'Nobody named' },
  { key: 'manifest', label: 'Manifest', value: () => null, labelFor: (v) => (v === 'yes' ? 'Read' : 'Unavailable') },
];

/**
 * ROW 356: the Archive's columns for the shared table. First-click direction per column: newest,
 * largest and slowest first for time, counts and TAT; A to Z for words.
 */
function archiveColumns(openId: string | null): GridColumn<ReviewBatchSummary>[] {
  return [
    {
      key: 'open', label: '', menuLabel: 'Open marker', locked: true, thStyle: { width: 28 },
      tdStyle: { textAlign: 'center', color: 'var(--ink-3)' },
      render: (b) => (openId === b.batchId ? '▾' : '▸'),
    },
    { key: 'approved', label: 'Approved', firstDir: 'desc', tdStyle: { whiteSpace: 'nowrap' }, render: (b) => fmtTs(b.approvedTs) },
    {
      key: 'bundle', label: 'Bundle', firstDir: 'desc',
      render: (b) => (
        <>
          {b.batchId}
          {!b.hasManifest && <span style={{ color: 'var(--ink-3)', marginLeft: 6, fontSize: '0.85em' }}>(manifest unavailable)</span>}
        </>
      ),
    },
    { key: 'members', label: 'Members', firstDir: 'desc', thClassName: 'num-cell', tdClassName: 'num-cell', render: (b) => b.members || b.count },
    { key: 'dispositions', label: 'Dispositions', render: (b) => <ActionMix actions={b.actions} /> },
    { key: 'operators', label: 'Handled by', render: (b) => b.operators.map(shortName).join(', ') || '-' },
    { key: 'reviewers', label: 'Reviewed by', render: (b) => b.reviewers.map(shortName).join(', ') || '-' },
    {
      key: 'tat', label: 'Avg TAT', firstDir: 'desc', thClassName: 'num-cell', tdClassName: 'num-cell',
      render: (b) => (b.avgTatMinutes != null ? fmtMinutes(b.avgTatMinutes) : '-'),
    },
  ];
}

function BatchDetail({ batchId }: { batchId: string }) {
  const [rows, setRows] = useState<ReviewBatchRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    getReviewBatch(batchId)
      .then((r) => {
        if (live) setRows(r.rows);
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : 'Could not load the bundle manifest.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [batchId]);

  if (loading) return <div className="note" style={{ margin: '8px 0' }}>Loading bundle...</div>;
  if (error) return <div className="note" style={{ margin: '8px 0' }}>{error}</div>;
  if (!rows || !rows.length) return <div className="note" style={{ margin: '8px 0' }}>This bundle has no manifest rows.</div>;

  return (
    <table className="dt" style={{ margin: '4px 0 10px' }}>
      <thead>
        <tr>
          <th>Member</th>
          <th>Member ID</th>
          <th>Dispositions</th>
          <th>Received</th>
          <th>Approved</th>
          <th>TAT</th>
          <th>Sent to Portal</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.eventId}>
            <td>{r.memberName || <span style={{ color: 'var(--ink-3)' }}>Unmatched</span>}</td>
            <td>{r.memberId ? fmtMemberId(r.memberId) : '-'}</td>
            <td>
              {(r.actions ?? []).length
                ? (r.actions ?? []).map(actionLabel).join(', ')
                : <span style={{ color: 'var(--ink-3)' }}>Handled - no action</span>}
            </td>
            <td>{fmtDate(r.receivedTs)}</td>
            <td>{fmtTs(r.approvedTs)}</td>
            <td>{Number.isFinite(r.tatMinutes) ? fmtMinutes(r.tatMinutes) : '-'}</td>
            <td>
              {r.handoffRef ? (
                <span
                  title={r.handoffRef}
                  style={{
                    display: 'inline-block',
                    padding: '1px 8px',
                    borderRadius: 999,
                    background: 'var(--teal-1, var(--paper-2))',
                    border: '1px solid var(--rule)',
                    fontSize: '0.82em',
                  }}
                >
                  Sent
                </span>
              ) : (
                <span style={{ color: 'var(--ink-3)' }}>-</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Archive() {
  const [batches, setBatches] = useState<ReviewBatchSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [facets, setFacets] = useState<Record<string, Array<{ v: string; n: number }>>>({});
  const [sliceHidden, setSliceHidden] = useState(0);

  // Row 356: the shared table; its sort (up to three keys) and slicers go to the server, remembered.
  const view = useGridView('archive', archiveColumns(openId), {
    defaultSort: [{ k: 'approved', asc: false }],
    slicers: ARCHIVE_SLICERS,
    serverSortable: ARCHIVE_SORT_KEYS,
  });
  const sortWire = sortParam(view.sort);
  const sliceWire = slicerParam(view.slicers);
  const isNewestFirst = sortWire === 'approved:desc';

  /**
   * Re-sorting refetches from the top rather than reordering what is loaded: the server orders all
   * 236 bundles and then cuts the page, so sorting here would only reorder the loaded 60. Cheap in
   * practice - the API memoizes bundle summaries, so a re-sort is the folder listing plus cache hits.
   * Row 356: a slicer does the same, for the same reason.
   */
  const load = useCallback(() => {
    setLoading(true);
    setError('');
    listReviewBatches({ sort: sortWire, ...(sliceWire ? { slice: sliceWire } : {}) })
      .then((r) => {
        setBatches(r.batches);
        setTotal(r.total);
        setFacets(r.facets ?? {});
        setSliceHidden(r.sliceHidden ?? 0);
        setOpenId(null); // a re-fetch can reshuffle; don't leave a detail row open on a stale id
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load the archive.'))
      .finally(() => setLoading(false));
  }, [sortWire, sliceWire]);

  /**
   * Append the next page. The API summarizes one bundle per Graph round trip, so the index arrives in
   * pages rather than in one request that would time out on a large archive (the 2026-08-03 500).
   */
  const loadMore = useCallback(() => {
    setLoadingMore(true);
    setError('');
    listReviewBatches({ offset: batches.length, sort: sortWire, ...(sliceWire ? { slice: sliceWire } : {}) })
      .then((r) => {
        setTotal(r.total);
        // Dedupe by batchId: a bundle archived between two pages shifts the window by one, which
        // would otherwise re-deliver the row on the seam.
        setBatches((prev) => {
          const seen = new Set(prev.map((b) => b.batchId));
          return [...prev, ...r.batches.filter((b) => !seen.has(b.batchId))];
        });
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load more bundles.'))
      .finally(() => setLoadingMore(false));
  }, [batches.length, sortWire, sliceWire]);

  useEffect(() => {
    load();
  }, [load]);

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? batches.filter((b) => {
        const hay = [
          b.batchId,
          ...b.actions.map((a) => actionLabel(a.type)),
          ...b.operators,
          ...b.reviewers,
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(needle);
      })
    : batches;

  const totalPackages = batches.reduce((n, b) => n + b.count, 0);
  const more = Math.max(total - batches.length, 0);
  // Search only sees what has been loaded, so say so rather than implying an empty result is the
  // whole archive.
  const searchIsPartial = !!needle && more > 0;

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">Member Care &middot; History</div>
        <h1 className="section-title">
          Archive
        </h1>
        <div className="section-rule" />
        <p className="section-lede">
          Every package sent to the Portal, kept as a bundle. Each row is one Final Review batch
          approval: the members it cleared, what was done, and the resolution shipped to the Portal.
          Read-only.{' '}
          {total
            ? more
              ? // "newest" only describes the default order; any other sort is just the top N of it.
                `${total} bundles; showing the ${isNewestFirst ? 'newest' : 'first'} ${batches.length}.`
              : `${total} bundle${total > 1 ? 's' : ''}, ${totalPackages} resolution${totalPackages > 1 ? 's' : ''}.`
            : ''}
        </p>
      </header>
      {/* R. 82 / row 401: the second view, the closed entries, lives at /archive/entries. */}
      <ArchiveTabs active="bundles" />
      {/* Row 354: its rows carry no campaign yet, so a campaign selection is SAID not to apply here. */}
      <NotByCampaignNote screen="The Archive" />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
          Refresh
        </button>
        <input
          className="text-input"
          type="search"
          aria-label="Search the archive"
          style={{ marginLeft: 'auto', maxWidth: 340 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search batch / action / operator / reviewer"
        />
      </div>

      {error && <div className="note" style={{ marginBottom: 14 }}>{error}</div>}

      <CareGrid
        view={view}
        rows={shown}
        rowKey={(b) => b.batchId}
        noun="bundles"
        server={{ facets, hidden: sliceHidden }}
        rowProps={(b) => ({
          onClick: () => setOpenId(openId === b.batchId ? null : b.batchId),
          style: { cursor: 'pointer' },
          'aria-expanded': openId === b.batchId,
        })}
        after={(b) =>
          openId === b.batchId ? (
            <div style={{ background: 'var(--paper-2)', padding: '8px 12px' }}>
              <BatchDetail batchId={b.batchId} />
            </div>
          ) : null
        }
        empty={
          loading ? 'Loading archive...'
            : batches.length ? 'No bundles match your search.' : 'Nothing archived yet. Approved review batches will appear here.'
        }
      />

      {!loading && more > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
          <span style={{ color: 'var(--ink-3)', fontSize: '0.88em' }}>
            {searchIsPartial
              ? `Showing ${batches.length} of ${total} bundles. Your search covers only the ones loaded - load more to search further back.`
              : `Showing ${batches.length} of ${total} bundles.`}
          </span>
        </div>
      )}
    </div>
  );
}
